import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@env';
import { Router } from '@angular/router';

/**
 * Opciones a enviar en peticiones al Back-End. Incluye cabecera y envío de credenciales
 */
const urlOptions = {
	withCredentials: true,
	headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json'})
};

const silencedErrors = [
	404
];

/**
 * Servicio injectable para realizar peticiones al back-end
 */
@Injectable()
export class HttpService {

	constructor(
		private http: HttpClient,
		private router: Router,
		private snackBar: MatSnackBar
	) {}

	/** URL base para ejecución de scripts PHP. Utiliza variable environment que cambia de absoluto (dev) a relativo (prod) */
	baseUrl = environment[environment.alias[window.location.hostname]].baseUrl;

	/**
	 * Loguea un usuario en el sistema
	 * @param user - Nombre de usuario
	 * @param pass - Contraseña del usuario
	 * @response {valid: boolean, razsocial: string}
	 */
	login(user: string, pass: string) {
		return this.http.post<any>(this.baseUrl + 'macnolog.php?login', `usuario=${user}&contra=${pass}`, urlOptions);
	}

	/**
	 * Desloguea usuario del sistema
	 * @response {out: boolean, razsocial: string}
	 */
	logout() {
		return this.http.get<any>(this.baseUrl + `macnolog.php?logout`, urlOptions);
	}

	/**
	 * Busca datos para cargar en el hojeador, según haya búsqueda o no
	 * @param que - Objeto a buscar
	 * @param page - Número de página del hojeador
	 * @param datos - Datos para el filtro: fecha, texto de búsqueda
	 * @response {todas: any[], cuantas: number}
	 */
	async browser(que: string, page: number, datos: any) {
		return await this.post(`hojear/${que}`, Object.assign({ pag: page }, datos));
	}

	/**
	 * Trae los datos pedidos filtrados por coincidencia con el texto provisto
	 * @param que - Qué busca
	 * @param texto - Texto a buscar
	 * @param datos (opcional) - Parámetros adicionales
	 * @example autocomplete('rubros','activo')
	 * @return Array con resultados
	 */
	async autocomplete(que: string, texto: string, datos?: any) {
		if (!datos) {
			datos = {};
		}
		return await this.get(`autocomplete/${que}`, Object.assign({ texto: texto }, datos));
	}

	async get(url: string, datos: any = null) {
		const options = datos ? this.paramsRequest(datos) : urlOptions;
		try {
			const res = await this.http.get<any>(this.baseUrl + url, options).toPromise();
			return res;
		} catch (error) {
			return this.error(error);
		}
	}

	async post(url: string, datos: any = null) {
		try {
			const res = await this.http.post<any>(this.baseUrl + url, datos, urlOptions).toPromise();
			return res;
		} catch (error) {
			return this.error(error);
		}
	}

	async put(url: string, datos: any = null) {
		try {
			const res = await this.http.put<any>(this.baseUrl + url, datos, urlOptions).toPromise();
			return res;
		} catch (error) {
			return this.error(error);
		}
	}

	async delete(url: string) {
		try {
			const res = await this.http.delete<any>(this.baseUrl + url, urlOptions).toPromise();
			return res;
		} catch (error) {
			return this.error(error);
		}
	}


	async file(url: string, formData: FormData, optOptions?: any) {
		const postOptions =  (optOptions) ? optOptions : urlOptions;
		try {
			const res = await this.http.post(this.baseUrl + url, formData, postOptions).toPromise();
			return res;
		} catch (error) {
			return this.error(error);
		}
	}

	private paramsRequest(datos: any) {
		let params = new HttpParams();
		for (const key in datos) {
			if (datos.hasOwnProperty(key)) {
				params = params.set(key, datos[key]);
			}
		}
		return Object.assign({params: params}, urlOptions);
	}

	private error(err) {
		console.error(err);
		if (err.status === 401) { // Interceptor login..
			this.router.navigate(['/auth/login']);
		} else if (err.url.indexOf('auth/') === -1 && silencedErrors.indexOf(err.status) === -1) {
			this.snackBar.open('Error', null, {
				duration: 5000,
				horizontalPosition: 'end',
				verticalPosition: 'top'
			});
		}
		throw err;
	}

	async leeUrl(dataURI) {
		const res = await this.http.request('GET', this.baseUrl + dataURI, { responseType: 'blob' }).toPromise();
		return res;
	}


	leeArchivo(input, readAs, onerror: Function) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				resolve(reader.result);
			};
			reader.onerror = () => {
				onerror();
				reject();
			};
			switch (readAs) {
				case 'text':
					reader.readAsText(input.files[0]);
					break;
				case 'dataurl':
					reader.readAsDataURL(input.files[0]);
					break;
				case 'urltodata':
					reader.readAsDataURL(this.baseUrl + input);
					break;
			}
		});
	}

}
